import React, { useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { User } from 'firebase/auth';

// Database character limits (based on Firestore document size constraints)
const CHARACTER_LIMITS = {
  question: 500,  // Reasonable limit for question field
  details: 2000   // More space for additional context
};

export interface DecisionCreatorProps {
  onSubmit?: (data: { question: string; details?: string; visibility?: 'public' | 'unlisted' }) => void;
  isSubmitting?: boolean;
  user: User;
  onError?: (error: string) => void;
  initialData?: {
    question: string;
    details?: string;
    visibility?: 'public' | 'unlisted';
  };
  isEditing?: boolean;
}

export const DecisionCreator: React.FC<DecisionCreatorProps> = ({ 
  onSubmit,
  isSubmitting = false,
  initialData,
  isEditing = false
}) => {
  const [question, setQuestion] = React.useState(initialData?.question || '');
  const [details, setDetails] = React.useState(initialData?.details || '');
  const questionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const detailsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [visibility, setVisibility] = React.useState<'public' | 'unlisted'>(
    initialData?.visibility || 'unlisted'
  );

  

  // Auto-resize functionality
  const resizeTextarea = (textareaRef: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight with a minimum height
      textarea.style.height = `${Math.max(textarea.scrollHeight, textarea.offsetHeight)}px`;
    }
  };

  // Auto-resize on content change
  useEffect(() => {
    resizeTextarea(questionTextareaRef);
  }, [question]);

  useEffect(() => {
    resizeTextarea(detailsTextareaRef);
  }, [details]);

  // Handle question change with character limit
  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= CHARACTER_LIMITS.question) {
      setQuestion(value);
    }
  };

  // Handle details change with character limit
  const handleDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= CHARACTER_LIMITS.details) {
      setDetails(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.({ question, details, visibility });
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6 px-4 md:px-8"> 
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="space-y-6">
            <div className="space-y-3">
              <label htmlFor="question" className="text-xl font-medium">
                What question do you want your group to answer?
              </label>
              <Textarea
                ref={questionTextareaRef}
                id="question"
                placeholder="e.g., What should our hybrid work policies be?"
                value={question}
                onChange={handleQuestionChange}
                className="min-h-[60px] md:min-h-[60px] w-full text-md placeholder:text-md placeholder:text-muted-foreground resize-none overflow-hidden"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label htmlFor="details" className="text-xl font-medium">
                  Additional Context
                </label>
                <span className="text-base text-muted-foreground">optional</span>
              </div>
              <Textarea
                ref={detailsTextareaRef}
                id="details"
                placeholder="Add any details or specific points you'd like participants to consider, like predefined options, cost, or how the summary of the group's views may be used."
                value={details}
                onChange={handleDetailsChange}
                className="min-h-[120px] text-md leading-relaxed placeholder:text-md placeholder:text-muted-foreground resize-none overflow-hidden"
              />
            </div>
  
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <label className="text-xl font-medium">
                  Visibility
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div
                  className={`p-4 border rounded-md flex items-center cursor-pointer ${
                    visibility === 'unlisted' ? 'border-primary bg-primary/5' : 'border-input'
                  }`}
                  onClick={() => setVisibility('unlisted')}
                >
                  <div className="flex items-center justify-center w-5 h-5 rounded-full border mr-3">
                    {visibility === 'unlisted' && (
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">Unlisted</p>
                    <p className="text-sm text-muted-foreground">Only accessible via direct link</p>
                  </div>
                </div>
                <div
                  className={`p-4 border rounded-md flex items-center cursor-pointer ${
                    visibility === 'public' ? 'border-primary bg-primary/5' : 'border-input'
                  }`}
                  onClick={() => setVisibility('public')}
                >
                  <div className="flex items-center justify-center w-5 h-5 rounded-full border mr-3">
                    {visibility === 'public' && (
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">Public</p>
                    <p className="text-sm text-muted-foreground">Visible in public listings</p>
                  </div>
                </div>
              </div>
            </div>
  
            <Button 
              type="submit" 
              disabled={!question.trim() || isSubmitting} 
              className="w-full h-14 text-lg"
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update decision' : 'Get group input')
              }
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default DecisionCreator;